import { DvmModule, MapViewerEvent, MapViewerService, NavigableMinimapComponent } from '@3ddv/ngx-dvm-internal';
import { DvmBBox, MapViewerEventObject, MapViewerNode, V2 } from '@3ddv/dvm-internal';
import { AfterViewInit, Component, computed, DestroyRef, effect, EffectRef, ElementRef, inject, signal, Signal, untracked, ViewChild, WritableSignal } from '@angular/core';
import { APP_CONFIG } from '@configuration/configuration';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { AppNodes } from '@models/enums/dvm/DvmNodes.enum';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { getSectionId, isInteriorViewId } from '@helpers/utils/dvm-node.util';
import { MenuService } from '@services/components/menu.service';
import { MinimapService } from '@services/components/minimap.service';
import { SearchService } from '@services/components/search.service';
import { DvmService } from '@services/dvm.service';
import { VenueConfigurationsService } from '@services/dvm/venue-configurations.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { merge, Observable, of } from 'rxjs';
import anime from 'animejs';
import { ViewerRouteParamsService } from '@services/viewer-route-params.service';
import { AmenitiesService } from '@services/amenities.service';
import { UiService } from '@services/ui.service';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';

@Component({
    selector: 'app-minimap',
    imports: [ DvmModule ],
    standalone: true,
    templateUrl: './minimap.component.html',
    styleUrl: './minimap.component.css'
})
export class MinimapComponent implements AfterViewInit{

  // VIEWCHILDS
  @ViewChild('minimapContainer')
  private readonly minimapContainer!: ElementRef<HTMLElement>
  
  @ViewChild('navigableMinimap', { static: false })
  public readonly  navigableMinimap!: NavigableMinimapComponent;
  
  // SERVICES
  private   readonly config:          Signal<Configuration> = inject(APP_CONFIG);
  private   readonly destroyRef:      DestroyRef            = inject(DestroyRef);
  public    readonly dvmService:      DvmService            = inject(DvmService);
  private   readonly menuService:     MenuService           = inject(MenuService);
  private   readonly minimapService:  MinimapService        = inject(MinimapService);
  private   readonly searchService = inject(SearchService, { optional: true });
  private   readonly hasViewId:       boolean              = inject(ViewerRouteParamsService).hasViewId;
  private   readonly venueConfigs:    VenueConfigurationsService = inject(VenueConfigurationsService);
  private   readonly amenities:       AmenitiesService      = inject(AmenitiesService);

  // GLOBAL STATE
  protected readonly view:            Signal<AppViews|null>     = this.dvmService.view;
  protected readonly minimap:         MapViewerService          = this.dvmService.minimap;
  // Config efectiva, no el flag global: sin esto se mostraria el mapa anterior, aun cargado en
  // `this.minimap`, cuando la configuracion activa no tiene minimapa propio.
  protected readonly enableMinimap:   Signal<boolean>           = computed((): boolean => this.venueConfigs.minimapConfig().enabled);
  protected readonly navigableMinimapEnabled:   Signal<boolean> = computed((): boolean => this.config()['Dvm']['minimap'].navigableMinimapEnabled);
  private   readonly uiConfig:                  Signal<UiConfiguration> = inject(UiService).uiConfig;
  private   readonly view3dDisplayEnabled:      Signal<boolean> = computed((): boolean => this.uiConfig().View3dDisplay.enabled);

  // APP VIEWS
  protected readonly appViews:        typeof AppViews           = AppViews;  
  
  // COMPONENT STATE
  protected readonly showMinimap:   WritableSignal<boolean>     = signal(false);
  protected readonly useNavigable:  WritableSignal<boolean>     = signal(false);
  // Espejo reactivo de service.isLoaded(): el SDK expone un metodo, no un signal
  protected readonly navigableLoaded: WritableSignal<boolean>    = signal(false);
  // Espejos reactivos del mapa y la pano cargados: view() no cambia entre seatmaps ni entre panos
  protected readonly hasSeats:        WritableSignal<boolean>     = signal(false);
  protected readonly currentView3d:   WritableSignal<string|null> = signal(null);
  protected readonly selectedSeatId:  WritableSignal<string|null> = signal(null);
  // Circulo "estas aqui": la vista de la pano o el asiento seleccionado en el seatmap (sin asiento, solo el sector resaltado)
  private   readonly hereId:          Signal<string|null>         = computed((): string|null => {
    if(this.useNavigable()) return null;
    const view: AppViews | null = this.view();
    const id: string | null = view === this.appViews.Pano ? this.currentView3d() : view === this.appViews.Seatmap ? this.selectedSeatId() : null;
    return isInteriorViewId(id) ? null : id;
  });
  // Posicion del circulo en px dentro de la caja del minimapa
  protected readonly herePosition:    WritableSignal<V2|null>     = signal(null);
  // Alto/ancho del mapa navegable; null hasta que carga
  protected readonly navigableAspect: WritableSignal<number|null> = signal(null);
  // Solo el navegable muestra el mapa entero; el normal siempre enfoca un sector y mantiene su caja
  protected readonly boxAspectRatio:  Signal<string|null>         = computed((): string|null => {
    const aspect: number | null = this.navigableAspect();
    return this.useNavigable() && aspect ? `1 / ${aspect}` : null;
  });
  private minimapAnimation: anime.AnimeInstance | null = null;
  private visibilityTarget: boolean = false;

  // GETTERS
  get isMinimapHidden(): boolean {
    return this.hasViewId || !this.showMinimap() || !this.enableMinimap();
  }

  // EFFECTS
  private  readonly onViewChange:      EffectRef = effect((): void => {    

    const view: AppViews | null   = this.view();
    let navigableEnabled: boolean = this.navigableMinimapEnabled();
    
    // Si tiene un view_id, no se hace nada
    if(!view || this.hasViewId) return;

    // load_success del SDK puede llegar despues del cambio de vista
    untracked((): void => this.syncLoadedState());
    
    // Si la vista es Main y el minimapa navegable esta habilitado, se habilita el minimapa navegable
    if(view === this.appViews.Main && untracked(this.enableMinimap)) navigableEnabled = true;
        
    this.useNavigable.set(navigableEnabled); // use navigable map if enabled

  });

  private readonly onVisibilityChange: EffectRef = effect((): void => {
    
    const show: boolean = this.shouldShowMinimap();
    
    this.animateVisibility(show);

  });

  // Al cambiar de vista o de asiento el minimapa puede no repintar: sin esto el circulo esperaria al siguiente 'update'
  private readonly onHereChange:       EffectRef = effect((): void => {
    this.hereId();
    untracked((): void => this.syncHerePosition());
  });

  // LIFECYCLE       
  ngAfterViewInit(): void {

    // Usamos shouldShowMinimap() y no el estado crudo del servicio: la visibilidad persiste en el
    // servicio root y no implica que esta instancia deba mostrarse (vista, minimapa cargado, etc.)
    this.animateVisibility(this.shouldShowMinimap());

    // LISTEN MAIN VIEWER AND 3D VIEWER LOADS
    this.dvmService.viewer.waitInitialize().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => {
      this.syncLoadedState();
      this.dvmService.viewer.getObservable('load_success').pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((): void => this.syncLoadedState());
      merge(
        this.dvmService.viewer.getObservable('select'),
        this.dvmService.viewer.getObservable('unselect')
      ).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((): void => this.syncSelectedSeat());
    });

    this.dvmService.viewer3d.waitInitialize().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => {
      this.syncLoadedState();
      this.dvmService.viewer3d.getObservable('load_success').pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((): void => this.syncLoadedState());
    });

    // LISTEN MINIMAP CLICKS (solo escritorio: en movil el canvas no recibe eventos)
    this.minimap.waitInitialize().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => {
      this.minimap.getObservable('click').pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((event: MapViewerEvent<MapViewerEventObject>): void => this.onCanvasClick(event.event.nodes[0]));
      // 'update' llega en cada frame: el circulo sigue al goTo y al expandir sin escuchar cada caso
      this.minimap.getObservable('update').pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((): void => this.syncHerePosition());
    });

    // LISTEN NAVIGABLE MINIMAP SERVICE INITIALIZATION
    this.navigableMinimap.service.waitInitialize().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => {

      // LOADED STATE
      this.syncNavigableLoaded();
      if(this.navigableLoaded()) this.syncNavigableAspect();

      merge(
        this.navigableMinimap.service.getObservable('load_start'),
        this.navigableMinimap.service.getObservable('load_success'),
        this.navigableMinimap.service.getObservable('load_error'),
        this.navigableMinimap.service.getObservable('reset')
      ).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((): void => this.syncNavigableLoaded());

      // LOAD SUCCESS: deja el flag listo por si la vista pasa a usar el minimapa normal (Main -> Seatmap)
      this.navigableMinimap.service.getObservable('load_success').pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((): void => {

        this.syncNavigableAspect();

        if(this.useNavigable()) this.minimapService.setVisibility(true);

      });

      // RESET
      this.navigableMinimap.service.getObservable('reset').pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((): void => {

        if(!this.useNavigable() && !this.hasViewId) this.minimapService.setVisibility(true);       
         
      });

    });

  }
 
  // METHODS
  private shouldShowMinimap(): boolean {

    const view: AppViews | null = this.view();

    if(this.hasViewId || !this.enableMinimap()) return false;

    if(this.useNavigable()) return view !== this.appViews.Pano && this.navigableLoaded();

    if(!this.minimapService.isVisible() || !this.minimap.isLoaded()) return false;

    // Pano: siempre salvo interiores. Seatmap: solo si el mapa tiene asientos. Topview: nunca
    if(view === this.appViews.Pano)    return !isInteriorViewId(this.currentView3d());
    if(view === this.appViews.Seatmap) return this.hasSeats();

    return false;

  }

  private syncLoadedState(): void {
    if(this.dvmService.viewer.isLoaded()) {
      this.hasSeats.set(this.dvmService.viewer.getTypesList().includes(AppNodes.Seat));
      this.syncSelectedSeat();
    }
    this.currentView3d.set(this.dvmService.currentView3d);
  }

  // Se lee del estado y no del evento: el click de asiento hace unselectAll + select
  private syncSelectedSeat(): void {
    this.selectedSeatId.set(this.dvmService.viewer.getNodesByState(AppNodes.Seat, 'selected')[0]?.id ?? null);
  }

  private syncHerePosition(): void {
    const node: MapViewerNode | null = this.getHereNode();
    // GA solo con el resaltado: sus areas pueden ser enormes o irregulares y el centro cae fuera
    const aabb: DvmBBox | null | undefined = node?.type === AppNodes.Ga ? null : node?.aabb;
    const position: V2 | null = aabb ? this.minimap.fromSceneToDom([aabb[0] + aabb[2] / 2, aabb[1] + aabb[3] / 2]) : null;
    const current: V2 | null = this.herePosition();
    // Solo si se mueve, para no repintar en cada frame
    if(position && current && Math.abs(position[0] - current[0]) < 0.5 && Math.abs(position[1] - current[1]) < 0.5) return;
    if(position || current) this.herePosition.set(position);
  }

  // Como en load3dView: el nodo si el minimapa lo tiene, si no su sector (los ids de GA llevan guiones)
  private getHereNode(): MapViewerNode | null {
    const id: string | null = this.hereId();
    if(!id || !this.minimap.isLoaded()) return null;
    return this.minimap.getNodeById(id) ?? this.minimap.getNodeById(getSectionId(id));
  }

  private syncNavigableLoaded(): void {
    this.navigableLoaded.set(this.navigableMinimap.service.isLoaded());
  }

  // La caja sigue la forma del mapa: como mucho tan alta como la de siempre (105/150) y sin quedar en una tira
  private syncNavigableAspect(): void {
    const bbox: DvmBBox | undefined = this.navigableMinimap.service.limits?.bbox_limits;
    if(!bbox?.[2] || !bbox[3]) return;
    const aspect: number = Math.min(Math.max(bbox[3] / bbox[2], 0.4), 0.7);
    // El plugin calcula el alto del canvas como ancho * aspect_ratio (por defecto 9/16, no el del mapa)
    this.navigableMinimap.service.aspect_ratio = aspect;
    this.navigableAspect.set(aspect);
  }

  private animateVisibility(show: boolean): void {

    const container: HTMLElement | undefined = this.minimapContainer?.nativeElement;

    if(!container) return;

    this.visibilityTarget = show;

    if(show && !this.showMinimap()) {
      requestAnimationFrame((): void => {
        if(!this.visibilityTarget) return;
        container.style.opacity = '0';
        container.style.transform = 'translateY(20px)';
        this.showMinimap.set(true);
        requestAnimationFrame((): void => {
          requestAnimationFrame((): void => this.runVisibilityAnimation(true));
        });
      });
      return;
    }

    this.runVisibilityAnimation(show);

  }

  private runVisibilityAnimation(show: boolean): void {

    const container: HTMLElement | undefined = this.minimapContainer?.nativeElement;

    if(!container) return;

    this.minimapAnimation?.pause();
    anime.remove(container);

    this.minimapAnimation = anime({
      targets:  container,
      opacity:  show ? 1 : 0,
      translateY: show ? 0 : 20,
      duration: 300,
      easing:   'easeInOutQuad',
      complete: (): void => {
        if(!show && !this.visibilityTarget) this.showMinimap.set(false);
      }
    });

  }

  // Solo clicks fuera del canvas (movil); los del canvas llegan por el evento click del SDK
  public onContainerClick(event: Event): void {

    if((event.target as Element | null)?.closest('dvm-map-viewer')) return;

    this.onMinimapClick();

  }

  public onMinimapClick(): void {

    // Si la vista es Main, no hacemos nada
    if(this.view() === AppViews.Main || this.useNavigable()) return; 

    this.searchService?.clearFocusedSection();

    // Forzamos el seteo null de un menu item y asi limpiamos filtros y ams si hay
    this.menuService.setMenuItem(null);

    // Reseteamos el viewer
    this.dvmService.resetViewer();

  }

  // Sector o GA navega; el fondo no hace nada para que un click fallido no lleve al inicio
  private onCanvasClick(node: MapViewerNode | undefined): void {

    if(!node || node.state === 'unavailable' || (node.type !== AppNodes.Section && node.type !== AppNodes.Ga)) return;

    this.navigateToSector(node);

  }

  // Mismo flujo que el click de sector en el topview (map-viewer)
  private navigateToSector(node: MapViewerNode): void {

    const sectorId:  string        = node.id;
    // En una pano el sector actual es el de su vista (p. ej. abierta desde el menu), no el mapa 2D de detras
    const viewId:    string | null = this.dvmService.is3d() ? this.dvmService.currentView3d : null;
    // Como en load3dView: el nodo si el minimapa lo tiene, si no su sector (los ids de GA llevan guiones)
    const currentId: string | null = viewId ? (this.minimap.getNodeById(viewId) ? viewId : getSectionId(viewId)) : this.dvmService.currentMap;

    // Sector actual: desde su pano basta con cerrarla
    if(sectorId === currentId) {
      if(this.dvmService.is3d()) this.dvmService.close3dView();
      return;
    }

    this.searchService?.clearFocusedSection();

    // El GA se detecta con el nodo del minimapa: apply3DView solo lo busca en el mapa principal
    const openPano: boolean = (node.type === AppNodes.Ga && this.view3dDisplayEnabled()) || this.dvmService.apply3DView(sectorId);

    // Sin 3D y sin seatmap (GA o excepcion GA) el topview solo selecciona: desde el minimapa no se hace nada
    if(!openPano && (node.type === AppNodes.Ga || this.dvmService.selectNodeInTopview(sectorId))) return;

    this.clearUnrelatedMenuItem(sectorId);

    if(openPano) {
      this.loadPanoOrSelect(sectorId);
      return;
    }

    // loadMap no cierra la pano por si solo
    if(this.dvmService.is3d()) this.dvmService.close3dView();

    this.dvmService.loadMap(sectorId).subscribe({
      next:  (): void => {
        const section: MapViewerNode | null = this.dvmService.viewer.getNodeById(sectorId);
        if(section) this.amenities.checkSectionAmenity(section.original_id);
      },
      error: (): void => this.loadPanoOrSelect(sectorId)
    });

  }

  // Como en navigateToSearch: un item de otro sector dejaria sus filtros aplicados en el destino
  private clearUnrelatedMenuItem(sectorId: string): void {
    const sections: string[] | undefined = this.menuService.itemSelected()?.relatedSeats?.section;
    if(!sections || sections.includes(sectorId)) return;
    this.menuService.clearCurrentSelection();
    // clearCurrentSelection no recalcula la disponibilidad: el mapa que queda bajo la pano seguiria filtrado
    this.dvmService.refreshAvailability();
  }

  private loadPanoOrSelect(sectorId: string): void {
    const pano$: Observable<boolean> = this.view3dDisplayEnabled() ? this.dvmService.load3dViewIfAvailable(sectorId) : of(false);
    pano$.subscribe({
      next:  (loaded: boolean): void => { if(!loaded) this.selectInTopview(sectorId); },
      error: (error: unknown): void => console.error('Error loading 3D view for minimap sector', sectorId, error)
    });
  }

  // Sin pano se selecciona en el topview, como desde su click; el mapa cargado puede ser el seatmap de otro sector
  private selectInTopview(sectorId: string): void {

    if(this.dvmService.is3d()) this.dvmService.close3dView();

    if(this.view() === AppViews.Topview && this.dvmService.viewer.getNodeById(sectorId)) {
      this.dvmService.replaceSelection(sectorId);
      return;
    }

    this.dvmService.loadMap().subscribe({
      next:  (): void => { if(this.dvmService.viewer.getNodeById(sectorId)) this.dvmService.replaceSelection(sectorId); },
      error: (error: unknown): void => console.error('Error loading the topview for minimap sector', sectorId, error)
    });

  }

}
